import { apiRequest } from "./apiClient";

export interface PrRequestRow {
  id: number;
  prId: string;
  supplier: string;
  category: string;
  description: string;
  amount: number | null;
  isRecurring: boolean;
  status: string;
  createdAt: string | null;
}

interface PrRequestApiRow {
  id: number;
  pr_id: string | null;
  supplier: string | null;
  category: string | null;
  estimated_amount: string | number | null;
  is_recurring: boolean | null;
  brief_description: string | null;
  status: string | null;
  created_at: string | null;
}

function mapRow(row: PrRequestApiRow): PrRequestRow {
  const amount = row.estimated_amount == null ? null : Number(row.estimated_amount);

  return {
    id: row.id,
    prId: row.pr_id || `PR-${row.id}`,
    supplier: row.supplier || "—",
    category: row.category || "—",
    description: row.brief_description || "—",
    amount: Number.isFinite(amount as number) ? (amount as number) : null,
    isRecurring: Boolean(row.is_recurring),
    status: row.status || "Pending",
    createdAt: row.created_at,
  };
}

export async function fetchPrRequests(): Promise<PrRequestRow[]> {
  const response = (await apiRequest("/api/supplier-pr-requests")) as {
    data: PrRequestApiRow[];
  };

  return (response?.data || []).map(mapRow);
}

export interface CreatePrPayload {
  supplier: string;
  supplierCode?: string;
  supplierDbId?: number;
  category?: string;
  estimatedAmount?: number | null;
  isRecurring?: boolean;
  briefDescription: string;
  prId?: string;
  status?: string;
}

export async function createPrRequest(payload: CreatePrPayload): Promise<PrRequestRow> {
  const response = (await apiRequest("/api/supplier-pr-requests", {
    method: "POST",
    body: JSON.stringify(payload),
  })) as { data: PrRequestApiRow };

  return mapRow(response.data);
}

export async function uploadIacAgentBackendData(): Promise<string> {
  const response = (await apiRequest("/api/iac-agent/backend-data", {
    method: "POST",
  })) as { url?: unknown };

  if (typeof response?.url !== "string" || !response.url.trim()) {
    throw new Error("IAC backend data upload did not return a pre-signed URL.");
  }

  return response.url;
}

export interface ResolvedSupplier {
  id: number;
  supplier: string;
  supplier_code?: string | null;
  default_description?: string | null;
  industry?: string | null;
  region?: string | null;
}

export async function resolveSupplier(
  value: string
): Promise<ResolvedSupplier | null> {
  try {
    const response = await apiRequest(
      `/api/thirdparty-suppliers/resolve/${encodeURIComponent(value)}`
    ) as {
      found: boolean;
      data?: ResolvedSupplier;
    };

    return response?.found ? response.data ?? null : null;
  } catch {
    return null;
  }
}
